// Synthetic play-by-play intelligence for the Riverside Thunder demo.
//
// The demo team is cloned into a throwaway Firestore session from the templates in
// demo-data.ts. The GameChanger pipeline (atBats → opponent-signals/season-signals
// → insights) normally only has data after a coach imports real GameChanger PDFs.
// This module hand-builds that pipeline output for the demo so EVERY session ships
// with a full opposition-research story already in place — no PDF import needed.
//
// The story centers on the WESTLAKE WARRIORS, the demo's rival:
//   • g1  (2026-03-15) — Thunder win 4-2  (away)
//   • g6  (2026-04-19) — Thunder lose 2-5 (home)
//   • g13 (2026-06-07) — the upcoming rematch the coach scouts in the demo
//
// Everything here is tuned to clear the exact thresholds in opponent-signals.ts and
// season-signals.ts so the deterministic engines surface the same intel they'd
// derive from real scorebooks: danger bats, soft spots, base-stealers, a pitcher who
// fatigues late, and a pitcher whose command collapses.
//
// opponentKey MUST equal reconcileGameImport's norm(): lowercase, alphanumeric only.
// "Westlake Warriors" → "westlakewarriors".

import { AtBatDoc, GameInsight, OpponentRecord } from '../types/dugout';

export const WESTLAKE_KEY = 'westlakewarriors';
export const WESTLAKE_NAME = 'Westlake Warriors';

// The two completed meetings, by their template game id (demo-data.ts).
// The persistence layer maps these template ids → real Firestore game doc ids.
export const WESTLAKE_GAME_IDS = ['g1', 'g6'] as const;

// ─── Pitch-sequence helpers ─────────────────────────────────────────────────
//
// opponent-signals.fatigueOf() needs ≥25 pitches per pitcher and reads strike% in
// the early vs late half. game-signals reads the same. We build honest sequences:
// a balls/strikes string → string[] of 'B'/'S'. Strikes lead to K, balls to BB.

type PSpec = string; // e.g. "SSS" (K looking/swinging), "BBBB" (walk), "BSX" (X=in play)

/** Expand a compact spec into the B/S pitch array the engine reads.
 *  'X' (ball in play) counts as a strike for strike% — it's a swing in the zone. */
function seq(spec: PSpec): string[] {
  return spec.split('').map(c => (c === 'B' ? 'B' : 'S'));
}

function countStrikes(pitches: string[]): number {
  return pitches.filter(p => p !== 'B').length;
}

// ─── At-bat builder ─────────────────────────────────────────────────────────

interface ABInput {
  gameId: string;
  inning: number;
  // batter
  batterTeam: 'us' | 'opp';
  batterId?: string;
  batterName: string;
  batterNumber: string;
  lineupSlot: number;
  // pitcher
  pitcherTeam: 'us' | 'opp';
  pitcherId?: string;
  pitcherName: string;
  pitcherNumber: string;
  // outcome
  resultCode: string;          // '1B' | '2B' | '3B' | 'HR' | 'BB' | 'K' | 'GO' | 'FO' | 'HBP' | 'E' | 'FC'
  spec: PSpec;                 // pitch sequence
  rbi?: number;
  runnersScored?: number;
  isLeadoff?: boolean;
  sb?: boolean;                // stole a base this AB → encoded in `result` for scout regex
}

const HIT = new Set(['1B', '2B', '3B', 'HR']);
const REACHED = new Set(['1B', '2B', '3B', 'HR', 'BB', 'HBP', 'E', 'FC']);

const RESULT_DESC: Record<string, string> = {
  '1B': 'Single', '2B': 'Double', '3B': 'Triple', 'HR': 'Home run',
  BB: 'Walk', K: 'Strikeout', GO: 'Groundout', FO: 'Flyout',
  HBP: 'Hit by pitch', E: 'Reached on error', FC: "Fielder's choice",
};

function makeAB(x: ABInput): AtBatDoc {
  const pitches = seq(x.spec);
  const strikes = countStrikes(pitches);
  const reached = REACHED.has(x.resultCode);
  // Firestore rejects any field whose value is `undefined`, so only include the
  // optional roster-id fields when they're actually set (our players, not opp).
  const doc: AtBatDoc = {
    gameId: x.gameId,
    inning: x.inning,
    batterTeam: x.batterTeam,
    batterName: x.batterName,
    batterNumber: x.batterNumber,
    lineupSlot: x.lineupSlot,
    pitcherTeam: x.pitcherTeam,
    pitcherName: x.pitcherName,
    pitcherNumber: x.pitcherNumber,
    opponentKey: WESTLAKE_KEY,
    result: x.sb ? `${x.resultCode} SB` : x.resultCode,
    resultCode: x.resultCode,
    resultDescription: RESULT_DESC[x.resultCode] || x.resultCode,
    reachedBase: reached,
    rbi: x.rbi ?? 0,
    runnersScored: x.runnersScored ?? 0,
    isLeadoff: x.isLeadoff ?? false,
    pitchSequence: pitches,
    balls: pitches.length - strikes,
    strikes,
    pitchCount: pitches.length,
    confidence: 'high',
  };
  if (x.batterId !== undefined) doc.batterId = x.batterId;
  if (x.pitcherId !== undefined) doc.pitcherId = x.pitcherId;
  return doc;
}

// ─── Westlake Warriors lineup (the opponent we scout) ───────────────────────
//
// Designed so the engine flags:
//   #4 Bryce Coleman — DANGER (extra-base pop, drives in runs)
//   #7 Diego Ramos   — DANGER (high on-base)
//   #2 Tanner Reeves  — base-stealer (leadoff burner)
//   #9 Cole Whitaker  — SOFT SPOT (we strike him out, he rarely reaches)
//
// Their pitchers:
//   #11 Gavin Pierce — STARTER who FATIGUES late (early strikes, loses the zone)
//   #23 Mason Hart   — RELIEVER with COMMAND COLLAPSE (gets ahead 0-1 then walks people)

const W = {
  reeves:   { name: 'Tanner Reeves',   number: '2',  slot: 1 },
  okafor:   { name: 'Jordan Okafor',   number: '5',  slot: 2 },
  blake:    { name: 'Sam Blake',       number: '12', slot: 3 },
  coleman:  { name: 'Bryce Coleman',   number: '4',  slot: 4 },
  nguyen:   { name: 'Kevin Nguyen',    number: '8',  slot: 5 },
  foster:   { name: 'Drew Foster',     number: '15', slot: 6 },
  ramos:    { name: 'Diego Ramos',     number: '7',  slot: 7 },
  abbott:   { name: 'Henry Abbott',    number: '21', slot: 8 },
  whitaker: { name: 'Cole Whitaker',   number: '9',  slot: 9 },
};

const W_PIERCE = { name: 'Gavin Pierce', number: '11' };
const W_HART   = { name: 'Mason Hart',   number: '23' };

// Our pitchers in the Westlake games (from demo-data PITCHERS_BY_GAME):
//   g1: Rivera (1-3), Wilson (4-6).   g6: Walsh (1-2), Garcia (3-4), Wilson (5-6).
const US_RIVERA = { id: 'dp_rivera', name: 'Marcus Rivera', number: '2' };
const US_WILSON = { id: 'dp_wilson', name: 'Caleb Wilson',  number: '5' };
const US_WALSH  = { id: 'dp_walsh',  name: 'Connor Walsh',  number: '21' };

// ── Opponent batting (their hitters vs OUR pitchers) ────────────────────────
//
// Helper to cut the boilerplate: opp batter facing one of our pitchers.
function oppBat(
  gameId: string, inning: number, b: { name: string; number: string; slot: number },
  ourP: { id: string; name: string; number: string },
  resultCode: string, spec: PSpec,
  extra: Partial<ABInput> = {},
): AtBatDoc {
  return makeAB({
    gameId, inning,
    batterTeam: 'opp', batterName: b.name, batterNumber: b.number, lineupSlot: b.slot,
    pitcherTeam: 'us', pitcherId: ourP.id, pitcherName: ourP.name, pitcherNumber: ourP.number,
    resultCode, spec, ...extra,
  });
}

// ── Our batting (our hitters vs THEIR pitchers) ─────────────────────────────
function usBat(
  gameId: string, inning: number,
  us: { id: string; name: string; number: string; slot: number },
  theirP: { name: string; number: string },
  resultCode: string, spec: PSpec,
  extra: Partial<ABInput> = {},
): AtBatDoc {
  return makeAB({
    gameId, inning,
    batterTeam: 'us', batterId: us.id, batterName: us.name, batterNumber: us.number, lineupSlot: us.slot,
    pitcherTeam: 'opp', pitcherName: theirP.name, pitcherNumber: theirP.number,
    resultCode, spec, ...extra,
  });
}

// Our lineup slots (top of the order matches demo-data narrative: Rivera/Park/Martinez
// set the table; Brooks/Walsh/Davis drive in).
const U = {
  rivera:   { id: 'dp_rivera',   name: 'Marcus Rivera',  number: '2',  slot: 1 },
  park:     { id: 'dp_park',     name: 'Ethan Park',     number: '8',  slot: 2 },
  martinez: { id: 'dp_martinez', name: 'Dylan Martinez', number: '4',  slot: 3 },
  brooks:   { id: 'dp_brooks',   name: 'Tyler Brooks',   number: '7',  slot: 4 },
  walsh:    { id: 'dp_walsh',    name: 'Connor Walsh',   number: '21', slot: 5 },
  davis:    { id: 'dp_davis',    name: 'Owen Davis',     number: '33', slot: 6 },
  garcia:   { id: 'dp_garcia',   name: 'Noah Garcia',    number: '17', slot: 7 },
  johnson:  { id: 'dp_johnson',  name: 'Liam Johnson',   number: '11', slot: 8 },
  lee:      { id: 'dp_lee',      name: 'Mason Lee',      number: '19', slot: 9 },
};

// ════════════════════════════════════════════════════════════════════════════
// GAME 1 — Thunder 4, Westlake 2 (away win). Rivera starts (1-3), Wilson (4-6).
// Westlake's Pierce starts and fatigues late; Hart relieves and walks people.
// ════════════════════════════════════════════════════════════════════════════

const G1_OPP: AtBatDoc[] = [
  // 1st — Pierce's lineup vs Rivera. Reeves steals, Coleman doubles.
  oppBat('g1', 1, W.reeves,   US_RIVERA, '1B', 'BSX', { isLeadoff: true, sb: true }),
  oppBat('g1', 1, W.okafor,   US_RIVERA, 'K',  'SSBS'),
  oppBat('g1', 1, W.blake,    US_RIVERA, 'GO', 'SX'),
  oppBat('g1', 1, W.coleman,  US_RIVERA, '2B', 'BSX', { rbi: 1, runnersScored: 1 }),
  // 2nd
  oppBat('g1', 2, W.nguyen,   US_RIVERA, 'FO', 'SBX'),
  oppBat('g1', 2, W.foster,   US_RIVERA, 'K',  'SSS'),
  oppBat('g1', 2, W.ramos,    US_RIVERA, 'BB', 'BBSB'),
  oppBat('g1', 2, W.abbott,   US_RIVERA, 'GO', 'BSX'),
  // 3rd
  oppBat('g1', 3, W.whitaker, US_RIVERA, 'K',  'SSS'),
  oppBat('g1', 3, W.reeves,   US_RIVERA, '1B', 'BSX'),
  oppBat('g1', 3, W.okafor,   US_RIVERA, 'FO', 'SX'),
  oppBat('g1', 3, W.blake,    US_RIVERA, 'K',  'BSSS'),
  // 4th — Wilson in
  oppBat('g1', 4, W.coleman,  US_WILSON, '1B', 'BBSX', { rbi: 1, runnersScored: 1 }),
  oppBat('g1', 4, W.nguyen,   US_WILSON, 'GO', 'SX'),
  oppBat('g1', 4, W.foster,   US_WILSON, 'FO', 'BSX'),
  oppBat('g1', 4, W.ramos,    US_WILSON, '1B', 'BBSX'),
  // 5th
  oppBat('g1', 5, W.abbott,   US_WILSON, 'K',  'SSBS'),
  oppBat('g1', 5, W.whitaker, US_WILSON, 'K',  'SSS'),
  oppBat('g1', 5, W.reeves,   US_WILSON, '1B', 'SX'),
  oppBat('g1', 5, W.okafor,   US_WILSON, 'GO', 'BX'),
  // 6th
  oppBat('g1', 6, W.blake,    US_WILSON, 'FO', 'SBX'),
  oppBat('g1', 6, W.coleman,  US_WILSON, '2B', 'BSX'),
  oppBat('g1', 6, W.nguyen,   US_WILSON, 'K',  'SSBS'),
  oppBat('g1', 6, W.ramos,    US_WILSON, 'BB', 'BBBB'),
];

const G1_US: AtBatDoc[] = [
  // vs Pierce (starter) early — he's sharp.
  usBat('g1', 1, U.rivera,   W_PIERCE, 'GO', 'SX', { isLeadoff: true }),
  usBat('g1', 1, U.park,     W_PIERCE, 'K',  'SSS'),
  usBat('g1', 1, U.martinez, W_PIERCE, 'BB', 'BSBB'),
  usBat('g1', 2, U.brooks,   W_PIERCE, '1B', 'BSX', { rbi: 1 }),
  usBat('g1', 2, U.walsh,    W_PIERCE, 'K',  'SSBS'),
  usBat('g1', 2, U.davis,    W_PIERCE, 'FO', 'SX'),
  usBat('g1', 3, U.garcia,   W_PIERCE, 'GO', 'SX'),
  usBat('g1', 3, U.johnson,  W_PIERCE, 'K',  'BSSS'),
  usBat('g1', 3, U.lee,      W_PIERCE, 'K',  'SSS'),
  // vs Pierce late — fatigue shows (more balls).
  usBat('g1', 4, U.rivera,   W_PIERCE, '2B', 'BBSX', { runnersScored: 1 }),
  usBat('g1', 4, U.park,     W_PIERCE, 'BB', 'BBBSB'),
  usBat('g1', 4, U.martinez, W_PIERCE, '1B', 'BBSX', { rbi: 1 }),
  // vs Hart (reliever, command collapse) — gets ahead then walks.
  usBat('g1', 5, U.brooks,   W_HART, 'BB', 'SBBBB'),
  usBat('g1', 5, U.walsh,    W_HART, '1B', 'SBX',  { rbi: 1 }),
  usBat('g1', 5, U.davis,    W_HART, 'BB', 'SBBBB'),
  usBat('g1', 6, U.garcia,   W_HART, 'K',  'SSBS'),
  usBat('g1', 6, U.johnson,  W_HART, 'BB', 'SBBBB'),
  usBat('g1', 6, U.lee,      W_HART, 'GO', 'SBX'),
];

// ════════════════════════════════════════════════════════════════════════════
// GAME 6 — Westlake 5, Thunder 2 (home loss). Walsh (1-2), Garcia (3-4), Wilson (5-6).
// This is the loss — Westlake's bats (Coleman, Ramos) do the damage.
// ════════════════════════════════════════════════════════════════════════════

const US_GARCIA = { id: 'dp_garcia', name: 'Noah Garcia', number: '17' };

const G6_OPP: AtBatDoc[] = [
  // 1st vs Walsh
  oppBat('g6', 1, W.reeves,   US_WALSH,  '1B', 'SX',   { isLeadoff: true, sb: true }),
  oppBat('g6', 1, W.okafor,   US_WALSH,  'GO', 'BSX'),
  oppBat('g6', 1, W.blake,    US_WALSH,  '1B', 'BSX'),
  oppBat('g6', 1, W.coleman,  US_WALSH,  '3B', 'BSX',  { rbi: 2, runnersScored: 2 }),
  oppBat('g6', 1, W.nguyen,   US_WALSH,  'FO', 'SX'),
  // 2nd
  oppBat('g6', 2, W.foster,   US_WALSH,  'K',  'SSS'),
  oppBat('g6', 2, W.ramos,    US_WALSH,  '2B', 'BBSX', { rbi: 1, runnersScored: 1 }),
  oppBat('g6', 2, W.abbott,   US_WALSH,  'GO', 'SX'),
  oppBat('g6', 2, W.whitaker, US_WALSH,  'K',  'SSBS'),
  // 3rd vs Garcia
  oppBat('g6', 3, W.reeves,   US_GARCIA, 'BB', 'BBBB',  { sb: true }),
  oppBat('g6', 3, W.okafor,   US_GARCIA, '1B', 'BSX'),
  oppBat('g6', 3, W.blake,    US_GARCIA, 'GO', 'SX'),
  oppBat('g6', 3, W.coleman,  US_GARCIA, '1B', 'BSX',  { rbi: 1, runnersScored: 1 }),
  // 4th
  oppBat('g6', 4, W.nguyen,   US_GARCIA, 'FO', 'SBX'),
  oppBat('g6', 4, W.foster,   US_GARCIA, 'GO', 'SX'),
  oppBat('g6', 4, W.ramos,    US_GARCIA, 'BB', 'BBSBB'),
  oppBat('g6', 4, W.abbott,   US_GARCIA, 'K',  'SSS'),
  // 5th vs Wilson
  oppBat('g6', 5, W.whitaker, US_WILSON, 'K',  'SSS'),
  oppBat('g6', 5, W.reeves,   US_WILSON, '1B', 'SX',   { sb: true }),
  oppBat('g6', 5, W.okafor,   US_WILSON, 'GO', 'BX'),
  oppBat('g6', 5, W.blake,    US_WILSON, 'FO', 'SX'),
  // 6th
  oppBat('g6', 6, W.coleman,  US_WILSON, '2B', 'BSX',  { rbi: 1, runnersScored: 1 }),
  oppBat('g6', 6, W.nguyen,   US_WILSON, 'K',  'SSBS'),
  oppBat('g6', 6, W.ramos,    US_WILSON, '1B', 'BBSX'),
  oppBat('g6', 6, W.whitaker, US_WILSON, 'K',  'SSS'),
];

const G6_US: AtBatDoc[] = [
  // vs Pierce (he starts again) — sharp early, Thunder cold.
  usBat('g6', 1, U.rivera,   W_PIERCE, '1B', 'BSX', { isLeadoff: true }),
  usBat('g6', 1, U.park,     W_PIERCE, 'K',  'SSS'),
  usBat('g6', 1, U.martinez, W_PIERCE, 'GO', 'SX'),
  usBat('g6', 2, U.brooks,   W_PIERCE, 'FO', 'BSX'),
  usBat('g6', 2, U.walsh,    W_PIERCE, 'K',  'SSBS'),
  usBat('g6', 2, U.davis,    W_PIERCE, 'GO', 'SX'),
  usBat('g6', 3, U.garcia,   W_PIERCE, 'K',  'SSS'),
  usBat('g6', 3, U.johnson,  W_PIERCE, '1B', 'BSX'),
  usBat('g6', 3, U.lee,      W_PIERCE, 'K',  'SSS'),
  // vs Pierce late — fatigue again; Thunder finally scratch runs.
  usBat('g6', 4, U.rivera,   W_PIERCE, 'BB', 'BBBSB'),
  usBat('g6', 4, U.park,     W_PIERCE, '1B', 'BBSX'),
  usBat('g6', 4, U.martinez, W_PIERCE, '2B', 'BSX', { rbi: 2, runnersScored: 1 }),
  // vs Hart (reliever) — walks again, but Thunder strand them in the loss.
  usBat('g6', 5, U.brooks,   W_HART, 'BB', 'SBBBB'),
  usBat('g6', 5, U.walsh,    W_HART, 'K',  'SSBS'),
  usBat('g6', 5, U.davis,    W_HART, 'BB', 'BSBBB'),
  usBat('g6', 6, U.garcia,   W_HART, 'GO', 'SBX'),
  usBat('g6', 6, U.johnson,  W_HART, 'BB', 'SBBBB'),
  usBat('g6', 6, U.lee,      W_HART, 'K',  'SSS'),
];

// All Westlake at-bats, keyed by template game id (persistence layer files them
// under the real Firestore game doc).
export const WESTLAKE_ATBATS: Record<string, AtBatDoc[]> = {
  g1: [...G1_OPP, ...G1_US],
  g6: [...G6_OPP, ...G6_US],
};

// ════════════════════════════════════════════════════════════════════════════
// SEASON-WIDE OUR-BATTER AT-BATS (g2–g5, g7–g11) — Game Plan data
// ════════════════════════════════════════════════════════════════════════════
//
// season-signals.aggregateBatterSignals() / suggestBattingOrder() roll our at-bats
// across ALL games. Westlake's two games alone are too thin (4 PA each → every slot
// flagged "small sample" and a noisy order). So we generate lightweight at-bats for
// the other completed games too, calibrated to each player's SEASON profile in
// demo-data.ts (OBP, K-rate, XBH-rate, patience) so the suggested order comes out
// realistic and stable: Rivera leads off, Walsh is the XBH run-producer, Lee is cold.
//
// These are our batters only (batterTeam:'us'); the opponents in these games have no
// scouting UI pointed at them, so we don't fabricate their hitters. opponentKey is the
// real opponent's normalized key for correctness, but it's never scouted in the demo.

// Per completed game (excluding the Westlake games g1/g6): opponent key + our pitcher
// matters only for labeling — order/insights read our batter rows.
const SEASON_GAMES: Array<{ id: string; oppKey: string; oppP: { name: string; number: string } }> = [
  { id: 'g2',  oppKey: 'eastsideeagles',      oppP: { name: 'Eagles Starter',     number: '0' } },
  { id: 'g3',  oppKey: 'northgatenavigators', oppP: { name: 'Navigators Starter', number: '0' } },
  { id: 'g4',  oppKey: 'hillcresthawks',      oppP: { name: 'Hawks Starter',      number: '0' } },
  { id: 'g5',  oppKey: 'creeksidecougars',    oppP: { name: 'Cougars Starter',    number: '0' } },
  { id: 'g7',  oppKey: 'ridgelinereds',       oppP: { name: 'Reds Starter',       number: '0' } },
  { id: 'g8',  oppKey: 'eastsideeagles',      oppP: { name: 'Eagles Starter',     number: '0' } },
  { id: 'g9',  oppKey: 'northgatenavigators', oppP: { name: 'Navigators Starter', number: '0' } },
  { id: 'g10', oppKey: 'hillcresthawks',      oppP: { name: 'Hawks Starter',      number: '0' } },
  { id: 'g11', oppKey: 'creeksidecougars',    oppP: { name: 'Cougars Starter',    number: '0' } },
];

// Each of our hitters' season profile, driving the per-AB outcome distribution.
// pa: per-game plate appearances. reach: P(reach base). xbh: P(extra-base | hit).
// k: P(strikeout | out). patience: avg pitches/PA (higher = grinder). The relative
// ORDER of `reach`/`patience` is what determines the suggested batting order, so it
// tracks each player's demo-data OBP/eye. Tuned to be internally consistent with the
// season stat lines in demo-data.ts without trying to reproduce them exactly.
interface BatProfile {
  us: { id: string; name: string; number: string; slot: number };
  pa: number; reach: number; xbh: number; k: number; patience: number;
}
// Spread the `reach` values clearly so season variance doesn't reshuffle the
// intended hierarchy (Rivera the table-setter on top, Walsh the run-producer with
// unambiguously the most pop → carved into the cleanup spot, Lee/Garcia cold).
const SEASON_PROFILES: BatProfile[] = [
  { us: U.rivera,   pa: 3, reach: 0.52, xbh: 0.30, k: 0.16, patience: 4.2 },
  { us: U.brooks,   pa: 3, reach: 0.46, xbh: 0.25, k: 0.24, patience: 3.8 },
  { us: U.martinez, pa: 3, reach: 0.44, xbh: 0.10, k: 0.14, patience: 4.5 },
  { us: U.park,     pa: 3, reach: 0.42, xbh: 0.15, k: 0.22, patience: 4.0 },
  { us: U.walsh,    pa: 3, reach: 0.36, xbh: 0.60, k: 0.40, patience: 3.6 },
  { us: U.davis,    pa: 3, reach: 0.31, xbh: 0.35, k: 0.42, patience: 3.7 },
  { us: U.johnson,  pa: 3, reach: 0.28, xbh: 0.12, k: 0.42, patience: 3.4 },
  { us: U.garcia,   pa: 3, reach: 0.26, xbh: 0.18, k: 0.45, patience: 3.5 },
  { us: U.lee,      pa: 2, reach: 0.20, xbh: 0.08, k: 0.58, patience: 3.2 },
];

// Tiny seeded RNG (mulberry32) so the generated season is identical every session.
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Build a pitch spec of a given length whose strike share rises with contact-y
// outcomes; ends in X (in play) for balls in play, last S for K, all B's for BB.
function specFor(rc: string, n: number, r: () => number): string {
  if (rc === 'BB') return 'B'.repeat(Math.max(4, n));
  if (rc === 'K') {
    const balls = Math.max(0, n - 3);
    return 'B'.repeat(Math.min(balls, r() < 0.5 ? 1 : 0)).padEnd(n - 1, 'S') + 'S';
  }
  // ball in play (hit or out): some balls + strikes, last pitch X.
  const balls = r() < 0.4 ? 1 : 0;
  return ('B'.repeat(balls) + 'S'.repeat(Math.max(0, n - 1 - balls))).slice(0, n - 1) + 'X';
}

function generateSeasonAtBats(): Record<string, AtBatDoc[]> {
  const out: Record<string, AtBatDoc[]> = {};
  const r = rng(20260616); // fixed seed → identical every demo session
  for (const game of SEASON_GAMES) {
    const abs: AtBatDoc[] = [];
    for (const p of SEASON_PROFILES) {
      for (let i = 0; i < p.pa; i++) {
        const inning = Math.min(6, i * 2 + 1 + Math.floor(r() * 2));
        const reached = r() < p.reach;
        let rc: string;
        if (reached) {
          if (r() < 0.18) rc = 'BB';
          else if (r() < p.xbh) rc = r() < 0.15 ? 'HR' : r() < 0.5 ? '2B' : '3B';
          else rc = '1B';
        } else {
          rc = r() < p.k ? 'K' : r() < 0.5 ? 'GO' : 'FO';
        }
        // pitch count around the patience target (±1)
        const n = Math.max(rc === 'BB' ? 4 : 1, Math.round(p.patience + (r() * 2 - 1)));
        const spec = specFor(rc, n, r);
        abs.push(makeAB({
          gameId: game.id, inning,
          batterTeam: 'us', batterId: p.us.id, batterName: p.us.name,
          batterNumber: p.us.number, lineupSlot: p.us.slot,
          pitcherTeam: 'opp', pitcherName: game.oppP.name, pitcherNumber: game.oppP.number,
          resultCode: rc, spec,
          isLeadoff: p.us.slot === 1 && i === 0,
        }));
      }
    }
    // these at-bats are vs this game's opponent, not Westlake — fix the key.
    abs.forEach(ab => { (ab as any).opponentKey = game.oppKey; });
    out[game.id] = abs;
  }
  return out;
}

const SEASON_ATBATS = generateSeasonAtBats();

// Everything the persistence layer files, keyed by template game id.
export const DEMO_ATBATS: Record<string, AtBatDoc[]> = {
  ...SEASON_ATBATS,
  ...WESTLAKE_ATBATS, // Westlake games carry the rich opp + us play-by-play
};

// ─── Opponent scouting record (drives HomeTab "N meetings · M known players") ──
//
// players keyed by jersey number, with the slots we've seen them hit in.
function buildWestlakeOpponentRecord(): OpponentRecord {
  const players: OpponentRecord['players'] = {};
  for (const b of Object.values(W)) {
    players[b.number] = { number: b.number, name: b.name, slotsSeen: [b.slot] };
  }
  return {
    key: WESTLAKE_KEY,
    name: WESTLAKE_NAME,
    firstSeen: '2026-03-15',
    lastSeen: '2026-04-19',
    gameIds: [...WESTLAKE_GAME_IDS], // template ids; persistence layer remaps to real ids
    players,
  };
}

export const WESTLAKE_OPPONENT_RECORD = buildWestlakeOpponentRecord();

// ─── Pre-written bench-coach insight cards ──────────────────────────────────
//
// Voice rule (hard constraint): suggestive bench coach, never corrective —
// "worth a look," never "you should have." One player, one fact, one suggestion.
// These mirror what the AI would narrate from the at-bats above.

function insight(
  gameId: string,
  o: Omit<GameInsight, 'gameId' | 'generatedAt' | 'dismissed' | 'tier' | 'source'>,
): GameInsight {
  return {
    gameId,
    generatedAt: '2026-04-19T20:00:00.000Z',
    dismissed: false,
    tier: 'game',
    source: 'ai',
    ...o,
  };
}

export const WESTLAKE_INSIGHTS: Record<string, GameInsight[]> = {
  g1: [
    insight('g1', {
      type: 'pitching_fatigue',
      subjectPlayerName: 'Gavin Pierce (Westlake #11)',
      inningLabel: 'Top 4th',
      prose: "Pierce was filling the zone early (first-pitch strikes to seven of his first nine), but the back half of his outing told a different story. His strike rate fell off a cliff once he crossed roughly 45 pitches, and that's exactly when the Thunder bats started squaring him up. Worth remembering: he's beatable late.",
      benchmark: { value: '−19%', label: 'strike% drop late vs early:', comparison: 'a real fatigue cliff' },
      dataPoints: { earlyStrikePct: 0.71, lateStrikePct: 0.52 },
    }),
    insight('g1', {
      type: 'batting_standout',
      subjectPlayerId: 'dp_brooks',
      subjectPlayerName: 'Tyler Brooks',
      inningLabel: 'Bottom 2nd',
      prose: "Brooks stayed inside the ball and drove in the first run with a clean single up the middle off Pierce's fastball. He's been your most reliable bat with runners on, and this was another quiet RBI knock. Might be worth a longer look in an RBI spot against this club.",
      dataPoints: { rbi: 1, hardContact: true },
    }),
    insight('g1', {
      type: 'pitching_command',
      subjectPlayerName: 'Mason Hart (Westlake #23)',
      inningLabel: 'Bottom 5th',
      prose: "When Hart came on in relief he got ahead of nearly every hitter, and then couldn't put them away. Three walks in two innings, each after an 0-1 count. The Thunder did the right thing taking pitches and making him prove the zone. If you see him again, patience looks like the play.",
      benchmark: { value: '38%', label: 'walk rate in relief:', comparison: 'gets ahead, then loses it' },
      dataPoints: { walks: 3, firstPitchStrikePct: 0.78 },
    }),
  ],
  g6: [
    insight('g6', {
      type: 'batting_standout',
      subjectPlayerName: 'Bryce Coleman (Westlake #4)',
      inningLabel: 'Bottom 1st',
      prose: "Coleman did the damage again: a bases-clearing triple in the first set the tone, and he added a double later. He's hit for extra bases in essentially every meeting, and he sits in their 4-hole for a reason. When the lineup card comes out next time, he's the bat to game-plan around.",
      benchmark: { value: '4 XBH', label: 'in 2 games vs you:', comparison: 'their real danger bat' },
      dataPoints: { xbh: 4, rbi: 3 },
    }),
    insight('g6', {
      type: 'pitching_workload',
      subjectPlayerId: 'dp_walsh',
      subjectPlayerName: 'Connor Walsh',
      inningLabel: 'Top 1st',
      prose: "Walsh got the start but the first inning got away from him: the leadoff single, a steal, and Coleman's triple put three on the board before there were two outs. The stuff was fine; the sequencing to their top of the order is the thing to revisit. Might be a spot to have someone warming earlier next time.",
      dataPoints: { firstInningRuns: 3 },
    }),
    insight('g6', {
      type: 'baserunning',
      subjectPlayerName: 'Tanner Reeves (Westlake #2)',
      inningLabel: 'Top 1st',
      prose: "Reeves was a problem on the bases all afternoon. He reached three times and ran every time, swiping a bag on each. He's their engine at the top of the order. Holding him close, a quicker release, or a pitchout early might be worth having in your back pocket for the rematch.",
      benchmark: { value: '3 SB', label: 'in this game alone:', comparison: 'their table-setter' },
      dataPoints: { sb: 3, reached: 3 },
    }),
  ],
};
